import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Orders.css';
import { useAuth } from '../hooks/useAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

export default function Orders() {
  const { user, accessToken } = useAuth();
  const isCustomer = user?.role === 'Customer';
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [actionMessage, setActionMessage] = useState('');

  // Status Change State
  const [newStatus, setNewStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [courierName, setCourierName] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const token = accessToken || localStorage.getItem('token') || localStorage.getItem('access_token');
  const limit = 10;

  const fetchOrders = useCallback(async (background = false, signal) => {
    if (!token) return;
    try {
      if (!background) setLoading(true);
      const query = new URLSearchParams({
        skip: skip,
        limit: limit
      });
      
      if (filterStatus !== 'all') {
        query.append('status', filterStatus);
      }

      const response = await axios.get(
        `${API_BASE_URL}/orders?${query.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` }, signal }
      );
      
      const payload = response.data;
      const items = Array.isArray(payload) ? payload : (payload?.items || payload?.orders || []);
      setOrders(Array.isArray(items) ? items : []);
      setTotal(Number(payload?.total ?? payload?.count ?? items.length ?? 0));
      setError('');
    } catch (err) {
      if (axios.isCancel(err)) return;
      setError(err.response?.data?.detail || 'Failed to load orders');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [token, filterStatus, skip]);

  useEffect(() => {
    const controller = new AbortController();
    fetchOrders(false, controller.signal);
    const refresh = () => {
      if (document.visibilityState === 'visible') fetchOrders(true, controller.signal);
    };
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(refresh, 15000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, [fetchOrders]);

  const fetchOrderDetails = async (orderId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/orders/${orderId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSelectedOrder(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    if (!newStatus || !selectedOrder) return;
    setUpdatingStatus(true);
    setError('');
    setActionMessage('');
    const orderId = selectedOrder.order_id || selectedOrder._id || selectedOrder.id;

    try {
      const payload = {
        status: newStatus,
        notes: statusNotes || undefined,
        tracking_number: trackingNumber || undefined,
        courier_name: courierName || undefined,
        tracking_url: trackingUrl || undefined,
      };

      await axios.put(
        `${API_BASE_URL}/orders/${orderId}/status`,
        payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setActionMessage(`✓ Order status successfully updated to ${newStatus.toUpperCase()}`);
      await fetchOrderDetails(orderId);
      fetchOrders(true);
      setNewStatus('');
      setStatusNotes('');
      setTrackingNumber('');
      setCourierName('');
      setTrackingUrl('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update order status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = (status) => {
    const key = (status || '').toLowerCase();
    const colors = {
      draft: '#ffc107',
      pending: '#ffc107',
      confirmed: '#17a2b8',
      packed: '#20c997',
      shipped: '#007bff',
      delivered: '#28a745',
      cancelled: '#dc3545',
      refunded: '#6f42c1'
    };
    return colors[key] || '#6c757d';
  };

  const getStatusIcon = (status) => {
    const key = (status || '').toLowerCase();
    const icons = {
      draft: '📝',
      pending: '⏳',
      confirmed: '✓',
      packed: '📦',
      shipped: '🚚',
      delivered: '✓✓',
      cancelled: '✗',
      refunded: '↩'
    };
    return icons[key] || '○';
  };

  const downloadInvoice = async (order) => {
    try {
      const id = order.order_id || order._id || order.id;
      const response = await axios.get(`${API_BASE_URL}/orders/${encodeURIComponent(id)}/invoice`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url; link.download = `${order.order_number}.pdf`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setActionMessage('Invoice PDF downloaded.');
    } catch {
      setError('Invoice could not be downloaded. Please sign in and retry.');
    }
  };

  const trackShipment = async (order) => {
    if (order.tracking_url) {
      window.open(order.tracking_url, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      await navigator.clipboard.writeText(order.tracking_number);
      setActionMessage('Tracking number copied. Paste it on the courier tracking page.');
    } catch {
      setActionMessage(`Tracking number: ${order.tracking_number}`);
    }
  };

  if (selectedOrder) {
    return (
      <div className="order-detail-container">
        <button 
          onClick={() => setSelectedOrder(null)}
          className="btn btn-back"
        >
          ← Back to Orders
        </button>

        <h1>Order Details</h1>
        
        {error && <div className="alert alert-error">{error}</div>}
        {actionMessage && <div className="alert alert-success" role="status">{actionMessage}</div>}

        <div className="order-detail">
          <div className="detail-header">
            <div>
              <h2>Order #{selectedOrder.order_number}</h2>
              <p className="order-date">
                Placed on {new Date(selectedOrder.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="status-badge" style={{ backgroundColor: getStatusColor(selectedOrder.status || selectedOrder.order_status) }}>
              {getStatusIcon(selectedOrder.status || selectedOrder.order_status)} {(selectedOrder.status || selectedOrder.order_status || 'PENDING').toUpperCase()}
            </div>
          </div>

          {/* Status Update / Fulfillment Control Panel */}
          {!isCustomer && (
            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#1e293b', fontWeight: 'bold' }}>
                ⚙️ Process / Update Order Status
              </h3>
              <form onSubmit={handleUpdateStatus} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                  <div style={{ minWidth: '200px', flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#475569', marginBottom: '4px' }}>
                      Change Order Status:
                    </label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: '#fff'
                      }}
                    >
                      <option value="">-- Select New Status --</option>
                      <option value="confirmed">Confirmed (Accept Order)</option>
                      <option value="packed">Packed (Ready for Dispatch)</option>
                      <option value="shipped">Shipped (In Transit)</option>
                      <option value="delivered">Delivered (Completed)</option>
                      <option value="cancelled">Cancelled (Release Stock)</option>
                      <option value="returned">Returned</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>

                  <div style={{ minWidth: '250px', flex: 2 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#475569', marginBottom: '4px' }}>
                      Status Notes / Reason (Optional):
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Order verified, dispatched via Delhivery"
                      value={statusNotes}
                      onChange={(e) => setStatusNotes(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                {newStatus === 'shipped' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', background: '#eff6ff', padding: '12px', borderRadius: '6px' }}>
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#1e40af', marginBottom: '4px' }}>
                        Courier Name:
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Delhivery, Bluedart, DTDC"
                        value={courierName}
                        onChange={(e) => setCourierName(e.target.value)}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #bfdbfe', borderRadius: '4px', fontSize: '13px' }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: '180px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#1e40af', marginBottom: '4px' }}>
                        Tracking Number / AWB:
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. AWB123456789"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #bfdbfe', borderRadius: '4px', fontSize: '13px' }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#1e40af', marginBottom: '4px' }}>
                        Tracking URL (Optional):
                      </label>
                      <input
                        type="text"
                        placeholder="https://..."
                        value={trackingUrl}
                        onChange={(e) => setTrackingUrl(e.target.value)}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #bfdbfe', borderRadius: '4px', fontSize: '13px' }}
                      />
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button
                    type="submit"
                    disabled={!newStatus || updatingStatus}
                    style={{
                      padding: '8px 20px',
                      background: newStatus ? '#2563eb' : '#94a3b8',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      cursor: newStatus && !updatingStatus ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {updatingStatus ? 'Updating...' : 'Update Status'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="detail-grid">
            <div className="detail-section">
              <h3>Order Items</h3>
              <table className="items-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Grade</th>
                    <th>Qty</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedOrder.lines || selectedOrder.items || []).map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.title || item.zupc_code || item.model_name || 'Product'}</td>
                      <td>{item.grade || item.sales_grade || '-'}</td>
                      <td>{item.quantity}</td>
                      <td>₹{(item.unit_price || item.price || (item.line_total ? item.line_total / item.quantity : 0))?.toLocaleString() || '0'}</td>
                      <td>₹{(item.total_price || item.line_total)?.toLocaleString() || '0'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="detail-section">
              <h3>Shipping Address</h3>
              <div className="address-box">
                <p><strong>{selectedOrder.shipping_address?.name || selectedOrder.customer_name || 'Customer'}</strong></p>
                <p>{selectedOrder.shipping_address?.address1 || selectedOrder.shipping_address?.address || 'No address'}</p>
                {selectedOrder.shipping_address?.address2 && <p>{selectedOrder.shipping_address.address2}</p>}
                <p>{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.postal_code || selectedOrder.shipping_address?.pincode}</p>
                {selectedOrder.shipping_address?.landmark && (
                  <p>Landmark: {selectedOrder.shipping_address.landmark}</p>
                )}
                <p>Phone: {selectedOrder.shipping_address?.phone || selectedOrder.customer_phone || selectedOrder.phone || 'N/A'}</p>
                <p>Email: {selectedOrder.customer_email || selectedOrder.email || 'N/A'}</p>
              </div>
            </div>

            <div className="detail-section">
              <h3>Billing Details</h3>
              <div className="billing-box">
                <div className="billing-row">
                  <span>Subtotal:</span>
                  <span>₹{(selectedOrder.subtotal?.toLocaleString() || '0')}</span>
                </div>
                <div className="billing-row">
                  <span>Tax (18%):</span>
                  <span>₹{(selectedOrder.tax?.toLocaleString() || '0')}</span>
                </div>
                <div className="billing-row">
                  <span>Shipping:</span>
                  <span>{selectedOrder.shipping === 0 ? 'FREE' : `₹${selectedOrder.shipping}`}</span>
                </div>
                <div className="billing-row total">
                  <span>Total Amount:</span>
                  <span>₹{selectedOrder.total_amount?.toLocaleString() || '0'}</span>
                </div>
              </div>

              <div className="payment-info">
                <p>
                  <strong>Payment Method:</strong> {selectedOrder.payment_method?.toUpperCase() || 'CASHFREE'}
                </p>
                <p>
                  <strong>Payment Status:</strong> 
                  <span style={{ color: (selectedOrder.payment_status || '').toUpperCase() === 'COMPLETED' || (selectedOrder.payment_status || '').toUpperCase() === 'SUCCESS' ? '#28a745' : '#dc3545' }}>
                    {' '}{selectedOrder.payment_status || 'Pending'}
                  </span>
                </p>
                {selectedOrder.payment_reference && (
                  <p>
                    <strong>Payment Ref:</strong> {selectedOrder.payment_reference}
                  </p>
                )}
              </div>
            </div>

            {((selectedOrder.status || selectedOrder.order_status || '').toLowerCase() === 'delivered') && (
              <div className="detail-section">
                <h3>Invoice</h3>
                <p>Invoice Number: <strong>{selectedOrder.invoice_number}</strong></p>
                <button onClick={() => downloadInvoice(selectedOrder)} className="btn btn-small">Download Invoice</button>
              </div>
            )}

            {selectedOrder.tracking_number && (
              <div className="detail-section">
                <h3>Tracking</h3>
                <p>Tracking Number: <strong>{selectedOrder.tracking_number}</strong></p>
                <button onClick={() => trackShipment(selectedOrder)} className="btn btn-small">Track Shipment</button>
              </div>
            )}
          </div>

          {(() => {
            const curStatus = (selectedOrder.status || selectedOrder.order_status || '').toLowerCase();
            return (
              <div className="timeline">
                <h3>Order Timeline</h3>
                <div className="timeline-events">
                  <div className={`timeline-event ${curStatus === 'confirmed' || ['packed', 'shipped', 'delivered', 'cancelled'].includes(curStatus) ? 'completed' : ''}`}>
                    <div className="timeline-marker">✓</div>
                    <div className="timeline-content">
                      <h4>Order Created</h4>
                      <p>{new Date(selectedOrder.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>

                  <div className={`timeline-event ${['packed', 'shipped', 'delivered'].includes(curStatus) ? 'completed' : curStatus === 'confirmed' ? 'current' : ''}`}>
                    <div className="timeline-marker">✓</div>
                    <div className="timeline-content">
                      <h4>Order Confirmed</h4>
                      <p>Processing your order...</p>
                    </div>
                  </div>

                  <div className={`timeline-event ${['shipped', 'delivered'].includes(curStatus) ? 'completed' : curStatus === 'packed' ? 'current' : ''}`}>
                    <div className="timeline-marker">✓</div>
                    <div className="timeline-content">
                      <h4>Packed & Ready</h4>
                      <p>Your device is being packed</p>
                    </div>
                  </div>

                  <div className={`timeline-event ${curStatus === 'delivered' ? 'completed' : curStatus === 'shipped' ? 'current' : ''}`}>
                    <div className="timeline-marker">✓</div>
                    <div className="timeline-content">
                      <h4>Shipped</h4>
                      <p>On the way to your location</p>
                    </div>
                  </div>

                  <div className={`timeline-event ${curStatus === 'delivered' ? 'completed' : ''}`}>
                    <div className="timeline-marker">✓</div>
                    <div className="timeline-content">
                      <h4>Delivered</h4>
                      <p>Order completed</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  return (
    <div className="orders-container">
      <h1>{isCustomer ? 'My Orders' : 'Orders'}</h1>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="orders-filter">
        <button className="btn" disabled={loading || !token} onClick={() => fetchOrders()}>Refresh orders</button>
        <select 
          value={filterStatus} 
          onChange={(e) => { setFilterStatus(e.target.value); setSkip(0); }}
          className="filter-select"
        >
          <option value="all">All Orders</option>
          <option value="pending_payment">Pending Payment</option>
          <option value="pending">Pending</option>
          <option value="expired">Expired</option>
          <option value="draft">Draft</option>
          <option value="confirmed">Confirmed</option>
          <option value="packed">Packed</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading && <div className="loading">Loading orders...</div>}

      {orders.length === 0 ? (
        <div className="empty-orders">
          <p>{isCustomer ? 'No orders found' : 'No orders available for this workspace.'}</p>
          {isCustomer ? (
            <a href="/products" className="btn btn-primary">Start Shopping</a>
          ) : (
            <a href="/products" className="btn btn-primary">Open Product Management</a>
          )}
        </div>
      ) : (
        <>
          <div className="orders-list">
            {orders.map(order => (
              <div key={order.id || order._id} className="order-card">
                <div className="order-header">
                  <div>
                    <h3>Order #{order.order_number}</h3>
                    <p className="order-date">
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="order-status" style={{ backgroundColor: getStatusColor(order.status || order.order_status) }}>
                    {getStatusIcon(order.status || order.order_status)} {(order.status || order.order_status || 'PENDING').toUpperCase()}
                  </div>
                </div>

                <div className="order-body">
                  <div className="order-items">
                    <p className="items-count">
                      {(order.lines || order.items || []).length} item(s)
                    </p>
                    {(order.lines || order.items || []).slice(0, 3).map((item, idx) => (
                      <div key={idx} className="item-preview">
                        {item.title || item.zupc_code || item.model_name || 'Item'} ({item.quantity}x)
                      </div>
                    ))}
                    {(order.lines || order.items || []).length > 3 && (
                      <p className="more-items">+{(order.lines || order.items).length - 3} more</p>
                    )}
                  </div>

                  <div className="order-amount">
                    <strong>₹{(order.grand_total ?? order.total_amount)?.toLocaleString() || '0'}</strong>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      {order.payment_method?.toUpperCase() || 'CASHFREE'}
                    </div>
                  </div>
                </div>

                <div className="order-footer">
                  <button
                    onClick={() => fetchOrderDetails(order.order_id || order.id || order._id)}
                    className="btn btn-small btn-primary"
                    disabled={loading}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>

          {total > limit && (
            <div className="pagination">
              <button
                onClick={() => setSkip(Math.max(0, skip - limit))}
                disabled={skip === 0}
                className="btn btn-small"
              >
                ← Previous
              </button>
              <span className="page-info">
                Showing {skip + 1} to {Math.min(skip + limit, total)} of {total}
              </span>
              <button
                onClick={() => setSkip(skip + limit)}
                disabled={skip + limit >= total}
                className="btn btn-small"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
